import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { DevicesDataService } from '../../../../services/data/devices-data/devices-data.service';
import { TAB_VALUE } from '../../../../shared/components/listing-page/listing-page.types';
import {
  chartHealthSegmentsColors,
  commodityTableIcon,
  createDeviceBadgesFn,
  deviceTypeTableIcon,
  METERING_DEFAULT_SEARCH_TYPE,
  METERING_SEARCH_TYPES
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import {
  batteryStatusTooltip,
  deviceHealthTooltip,
  lastHeardTooltip,
  transmissionReadRateTooltip,
  validReadRateTooltip
} from '../../../../shared/components/listing-table/listing-page.consts';
import { ListingTableComponent } from '../../../../shared/components/listing-table/listing-table.component';
import { LocationColumnComponent } from '../../../../shared/components/table-columns/location-column.component';
import { MultiLineColumnComponent } from '../../../../shared/components/multi-line-column/multi-line-column.component';
import { DeviceSidePanelTitleComponent } from '../components/device-side-panel-title/device-side-panel-title.component';
import { DeviceSidePanelComponent } from '../components/device-side-panel/device-side-panel.component';
import { meteringTableSortField } from '../constants/metering.constants';
import { DeviceListingFieldName, DevicesPayload, OrderDirection, TableDevices } from '../components/devices.models';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { StatusDotColumnComponent } from 'src/app/shared/components/listing-table/components/status-dot-column.component';
import { BubbleColumnComponent } from 'src/app/shared/components/listing-table/components/bubble-column.component';
import { IconColumnComponent } from 'src/app/shared/components/listing-table/components/icon-column.component';
import { ExportCsvConfig, ExportDataService } from 'src/app/services/file/export-data.service';
import { MeterTable } from 'src/app/services/data/devices-data/devices-data.types';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { DevicesMapComponent } from '@portal/shared/components/map-report-group/map-report-group.component';
import { ListingPageContext } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-context.types';
import { Applicable } from 'src/app/pages/administration/report-groups/report-groups.types';
import { devicesListingSource, devicesWithoutSerialNumber } from '../constants/devices.constants';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { MeteringListingFiltersSourceService } from './filter-sources/metering-listing-filters-source.service';
import { AccountNumbersColumnComponent } from '../components/column/account-numbers-column.component';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { SerialNumberColumnComponent } from '../components/column/serial-number-column.component';
import { CommDeviceIdColumnComponent } from '../components/column/comm-device-id-column.component';
import { Commodity } from '@portal/shared/types/commodity.types';
import { Store } from '@ngrx/store';
import { isSingleCommodityClient } from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

@Injectable()
export class MeteringListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly devices = inject(DevicesDataService);
  private readonly exportService = inject(ExportDataService);
  private readonly timezoneService = inject(TimezoneService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly #isSingleCommodityClient = inject(Store).selectSignal(isSingleCommodityClient);

  readonly pageTitle = TranslateRegistry.get.METERING.TITLE.METERING;
  readonly searchTypeList = signal(METERING_SEARCH_TYPES);
  readonly selectedSearchType = signal(METERING_DEFAULT_SEARCH_TYPE);
  readonly sorting = signal({
    columnName: DeviceListingFieldName.LastHeard,
    orderDirection: OrderDirection.DESC
  });
  readonly tableColumns = computed(() => [
    {
      field: 'commDeviceId',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.COMM_DEVICE_ID,
      width: 200,
      sortable: false,
      cmp: CommDeviceIdColumnComponent
    },
    {
      field: 'serialNumber',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.SERIAL_NUMBER,
      width: 200,
      sortable: false,
      cmp: SerialNumberColumnComponent
    },
    {
      field: 'deviceTypeName',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.DEVICE_TYPE,
      width: 200,
      sortable: true,
      icon: deviceTypeTableIcon
    },
    {
      field: 'meterNumber',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.INVENTORY_ID,
      width: 200,
      sortable: true,
      text: (dataItem: MeterTable): string => `${dataItem.meterNumber ?? ''}`
    },
    {
      field: 'accountNumbers',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.ACCOUNT_NUMBER,
      width: 160,
      sortable: false,
      link: { getLink: (): string => 'link' },
      cmp: AccountNumbersColumnComponent
    },
    {
      field: 'serviceLocationIds',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.SERVICE_LOCATION_NUMBER,
      width: 120,
      sortable: false,
      cmp: MultiLineColumnComponent
    },
    {
      field: 'serviceLocationAddress',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.SERVICE_LOCATION_ADDRESS,
      width: 200,
      sortable: false,
      cmp: MultiLineColumnComponent
    },
    {
      field: 'commodity',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.COMMODITY,
      width: 126,
      sortable: true,
      icon: commodityTableIcon,
      cmp: IconColumnComponent,
      isHidden: this.#isSingleCommodityClient()
    },
    {
      field: 'portNumber',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.PORT,
      width: 200,
      sortable: false,
      text: (dataItem: MeterTable): string => `${dataItem.portNumber ?? ''}`
    },
    {
      field: 'model',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.MODEL,
      width: 400,
      sortable: true
    },
    {
      field: 'status',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.STATUS,
      width: 200,
      sortable: false,
      cmp: StatusDotColumnComponent
    },
    {
      field: 'deviceStatusValue',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.HEALTH,
      width: 200,
      sortable: true,
      cmp: BubbleColumnComponent,
      tooltip: deviceHealthTooltip
    },
    {
      field: 'lastHeard',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.LAST_HEARD,
      width: 200,
      sortable: true,
      tooltip: lastHeardTooltip,
      text: (dataItem: MeterTable): string => `${dataItem.lastHeard ?? ''}`
    },
    {
      field: 'batteryStatus',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.BATTERY_STATUS,
      width: 200,
      sortable: false,
      cmp: BubbleColumnComponent,
      tooltip: batteryStatusTooltip
    },
    {
      field: 'transmissionReadRateStatus',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.READ_RATE_STATUS,
      width: 200,
      sortable: false,
      cmp: BubbleColumnComponent,
      tooltip: transmissionReadRateTooltip
    },
    {
      field: 'validTransmissionReadRateStatus',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.VALID_READ_RATE_STATUS,
      width: 200,
      sortable: false,
      cmp: BubbleColumnComponent,
      tooltip: validReadRateTooltip
    },
    {
      field: 'location',
      title: TranslateRegistry.get.METERING.VIEW_TABLE.LOCATION,
      width: 200,
      sortable: false,
      cmp: LocationColumnComponent
    }
  ]);
  readonly dataSource: ListingDataSource<TableDevices> = {
    getAll: tableState => {
      return this.devices.prepareDataForTable(tableState);
    },
    total: 0
  };
  readonly exportCsv = {
    dataSource: {
      export: (payload: Pick<DevicesPayload, 'filters' | 'sort'>): Observable<ExportCsvConfig[]> => {
        return this.devices.export(payload).pipe(
          map(response => {
            return response.map(meter => {
              const accountNumbers =
                meter.servicePoints
                  ?.map(sp => sp.fullAccountNumber)
                  .filter(Boolean)
                  .join('; ') ?? '';
              const serviceLocationIds =
                meter.servicePoints
                  ?.map(sp => (sp.serviceLocationId ? `${sp.serviceLocationId}` : null))
                  .filter(Boolean)
                  .join('; ') ?? '';

              return {
                commDeviceId: {
                  title: 'CommDeviceID',
                  value: meter.commDeviceId ?? meter.parentCommDeviceId ?? ''
                },
                serialNumber: {
                  title: 'SerialNumber',
                  value: devicesWithoutSerialNumber.includes(meter.deviceType)
                    ? ''
                    : (meter.serialNumber ?? 'Unknown Serial Number')
                },
                deviceTypeName: {
                  title: 'DeviceType',
                  value: meter.deviceTypeName
                },
                meterNumber: {
                  title: 'InventoryID',
                  value: meter.meterNumber ?? ''
                },
                accountNumbers: {
                  title: 'AccountNumber',
                  value: accountNumbers
                },
                serviceLocationIds: {
                  title: 'ServiceLocationNumber',
                  value: serviceLocationIds
                },
                commodity: {
                  title: 'Commodity',
                  value: meter.commodity === Commodity.Electricity ? 'Electric' : meter.commodity
                },
                portNumber: {
                  title: 'Port',
                  value: meter.portNumber
                },
                model: {
                  title: 'Model',
                  value: meter.model
                },
                status: {
                  title: 'Status',
                  value: meter.status
                },
                deviceStatusValue: {
                  title: 'DeviceHealth',
                  value: meter.deviceStatusValue
                },
                lastHeard: {
                  title: 'LastHeard',
                  value: meter.lastHeard ? this.timezoneService.formatLong(meter.lastHeard) : ''
                },
                batteryStatus: {
                  title: 'BatteryStatus',
                  value: meter.batteryStatus
                },
                transmissionReadRateStatus: {
                  title: 'ReadRateStatus',
                  value: meter.transmissionReadRateStatus
                },
                validTransmissionReadRateStatus: {
                  title: 'ValidReadRateStatus',
                  value: meter.validTransmissionReadRateStatus
                },
                serviceLocationAddress: {
                  title: 'ServiceLocationAddress',
                  value:
                    meter.servicePoints
                      ?.map(sp => sp.serviceLocationAddress ?? '')
                      .filter(Boolean)
                      .join('; ') ?? ''
                },
                location: [
                  {
                    title: 'Latitude',
                    value: meter.geoMetaData?.latitude ?? ''
                  },
                  {
                    title: 'Longitude',
                    value: meter.geoMetaData?.longitude ?? ''
                  },
                  {
                    title: 'Comment',
                    value: meter.geoMetaData?.comments ?? ''
                  }
                ]
              } as ExportCsvConfig;
            });
          }),

          tap(data => {
            this.exportService.exportDeviceRecordToCsv(data, 'Export-Metering', this.pageId);
          })
        );
      }
    }
  };
  readonly filterSource = inject(MeteringListingFiltersSourceService);

  readonly selectionFieldName = 'deviceUID';
  readonly chartSegmentsColors = chartHealthSegmentsColors;
  readonly createBadgesFn = createDeviceBadgesFn;
  readonly mapCmp = DevicesMapComponent;
  readonly tableCmp = ListingTableComponent;
  readonly contentClasses: string[] = ['listing-page__content_breakpoint'];
  readonly defaultTab = TAB_VALUE.Table;
  readonly sortField = meteringTableSortField;
  readonly sidePanelTitleCmp = DeviceSidePanelTitleComponent;
  readonly sidePanelBodyCmp = DeviceSidePanelComponent;
  readonly createMapCmpInputs = (ctx: ListingPageContext): Record<string, unknown> => {
    return {
      selectedLocationData: ctx.selectedLocation(),
      filtersPayload: ctx.mapFilters(),
      chartSegmentsColors: this.chartSegmentsColors,
      createBadgesFn: this.createBadgesFn,
      locationSelectedFn: ctx.onLocationSelected,
      applicable: Applicable.AmiHealthDashboard,
      actionSource: devicesListingSource,
      pageId: this.pageId
    };
  };
  get pageId(): ListingPageId {
    return ListingPageId.Metering;
  }

  constructor() {
    super();
    const registry = this.listingPageModelRegistry;
    registry.add(ListingPageId.Metering, this);
  }
}
